import React from 'react';
import { MdOutlineHome, MdOutlineOpenInNew } from 'react-icons/md';
import { SectionCard, Pill, Chip, StatTile, FactGrid, SubHeading, Note, StatusPill } from './ui';
import ZoningSection from './ZoningSection';
import TerrainSection from './TerrainSection';
import {
  fmtMoney, fmtNum, fmtPct, fmtDate, fmtAcres, fmtSqft, fmtBool, titleCase, safeArray, isNum, DASH,
} from '../format';


const ACTIVE_STATUSES = ['FOR_SALE', 'ACTIVE', 'PENDING', 'FOR_RENT', 'COMING_SOON', 'CONTINGENT'];

export default function PropertyCard({ data, status, zoning, zoningStatus, terrain, terrainStatus, maxSlopePercent }) {
  const physical = data?.physical || {};
  const sp = data?.status_and_price || {};
  const val = data?.valuation_and_costs || {};
  const community = data?.community || {};
  const identity = data?.identity || {};
  const match = data?.address_match || null;
  const sources = safeArray(data?.sources_used);
  const listingUrl = data?.listing_url || null;

  const listingStatus = sp.listing_status || null;
  const isActive = listingStatus && ACTIVE_STATUSES.some((s) => String(listingStatus).toUpperCase().includes(s));
  let hero;
  if (isActive && isNum(sp.current_list_price_usd)) {
    hero = { label: 'List price', value: fmtMoney(sp.current_list_price_usd), sub: sp.listed_at ? `Listed ${fmtDate(sp.listed_at)}` : null };
  } else if (isNum(sp.last_sold_price_usd)) {
    hero = { label: 'Last sold', value: fmtMoney(sp.last_sold_price_usd), sub: sp.last_sold_date ? fmtDate(sp.last_sold_date) : null };
  } else if (isNum(sp.current_list_price_usd)) {
    hero = { label: 'Price', value: fmtMoney(sp.current_list_price_usd), sub: listingStatus ? titleCase(listingStatus) : null };
  } else if (isNum(val.estimated_value_usd)) {
    hero = { label: 'Estimated value', value: fmtMoney(val.estimated_value_usd), sub: 'Automated valuation' };
  } else {
    hero = { label: 'Price', value: DASH, sub: 'No price on record' };
  }

  const valueRange = isNum(val.estimated_value_usd) && isNum(val.estimated_value_low_percent) && isNum(val.estimated_value_high_percent)
    ? `${fmtMoney(val.estimated_value_usd * (1 - val.estimated_value_low_percent / 100), { compact: true })} – ${fmtMoney(val.estimated_value_usd * (1 + val.estimated_value_high_percent / 100), { compact: true })}`
    : null;

  const zoneCode = zoning?.district?.zone_code || null;
  const matchTone = match ? ({ exact: 'good', partial: 'warning', mismatch: 'critical' }[match.status] || 'neutral') : null;
  const matchLabel = match ? ({ exact: 'Address verified', partial: 'Address partly verified', mismatch: 'Address mismatch', unknown: 'Address unverified' }[match.status] || 'Address unverified') : null;
  const headerRight = (
    <>
      {zoneCode ? <Pill tone="info" title={zoning.district.zone_name || undefined}>Zoned {zoneCode}</Pill> : null}
      {zoningStatus && zoningStatus.status !== 'ok' && !zoneCode ? <StatusPill status={{ ...zoningStatus, status: zoningStatus.status }} /> : null}
      {match ? <Pill tone={matchTone} title={match.note || undefined}>{matchLabel}</Pill> : null}
    </>
  );
  // The card stays useful without a listing record: zoning stands on its own.
  const dataset = data || (zoning || terrain ? {} : null);

  const subtitle = identity.matched_address
    ? `Public listing record · ${[identity.matched_address.street, identity.matched_address.city, [identity.matched_address.state, identity.matched_address.postal_code].filter(Boolean).join(' ')].filter(Boolean).join(', ')}`
    : 'Public listing record';

  const lotValue = isNum(physical.lot_area_acres) || isNum(physical.lot_area_sqft)
    ? `${isNum(physical.lot_area_acres) ? fmtAcres(physical.lot_area_acres) : ''}${isNum(physical.lot_area_acres) && isNum(physical.lot_area_sqft) ? ' · ' : ''}${isNum(physical.lot_area_sqft) ? fmtSqft(physical.lot_area_sqft) : ''}`
    : null;

  return (
    <SectionCard id="le-section-property" icon={<MdOutlineHome size={20} />} title="Listing" subtitle={subtitle} status={status} dataset={dataset} headerRight={headerRight}>
      {!data && (zoning || terrain) ? (
        <Note tone="muted">No public listing record matched this location ({status?.status ? status.status.replace(/_/g, ' ') : 'unavailable'}); zoning, parcel and terrain still apply.</Note>
      ) : null}
      {data ? (
        <>
          <div className="le-chip-row">
            {physical.property_type ? <Chip>{titleCase(physical.property_type)}</Chip> : null}
            {physical.structure_type ? <Chip>{titleCase(physical.structure_type)}</Chip> : null}
            {listingStatus ? <Pill tone={isActive ? 'good' : 'neutral'}>{titleCase(listingStatus)}</Pill> : <Pill tone="muted">Status unknown</Pill>}
            {physical.is_new_construction === true ? <Pill tone="info">New construction</Pill> : null}
            {identity.parcel_number ? <Chip mono title="Parcel number">APN {identity.parcel_number}</Chip> : null}
          </div>

          <div className="le-stat-row le-stat-row-hero">
            <StatTile label={hero.label} value={hero.value} sub={hero.sub} emphasis size="lg" />
            <StatTile
              label="Estimated value"
              value={fmtMoney(val.estimated_value_usd)}
              sub={valueRange ? `Range ${valueRange}` : 'Automated valuation model'}
              hint="Automated valuation — an estimate, not an appraisal"
            />
            <StatTile label="Est. rent" value={fmtMoney(val.estimated_rent_usd_per_month)} sub={isNum(val.estimated_rent_usd_per_month) ? 'per month · modeled estimate' : 'Modeled estimate'} />
            <StatTile
              label="Price per sq ft"
              value={isNum(val.price_per_sqft_usd) ? fmtMoney(val.price_per_sqft_usd) : DASH}
              sub={val.price_per_sqft_basis ? `Basis: ${titleCase(val.price_per_sqft_basis)}` : null}
            />
          </div>

          <SubHeading>Home &amp; land</SubHeading>
          <FactGrid
            items={[
              { label: 'Lot size', value: lotValue },
              { label: 'Living area', value: isNum(physical.living_area_sqft) ? fmtSqft(physical.living_area_sqft) : null },
              { label: 'Bedrooms', value: isNum(physical.bedrooms) ? fmtNum(physical.bedrooms) : null },
              { label: 'Bathrooms', value: isNum(physical.bathrooms_total) ? fmtNum(physical.bathrooms_total, Number.isInteger(physical.bathrooms_total) ? 0 : 1) : null },
              { label: 'Year built', value: isNum(physical.year_built) ? String(physical.year_built) : null },
              { label: 'Stories', value: isNum(physical.stories) ? fmtNum(physical.stories) : null },
              { label: 'Parking', value: isNum(physical.parking_capacity) ? `${fmtNum(physical.parking_capacity)} spaces` : null },
              { label: 'Basement', value: fmtBool(physical.has_basement) },
              { label: 'Pool', value: fmtBool(physical.has_pool) },
              { label: 'New construction', value: fmtBool(physical.is_new_construction) },
            ]}
          />

          <ZoningSection zoning={zoning} status={zoningStatus} />
          <TerrainSection terrain={terrain} status={terrainStatus} maxSlopePercent={maxSlopePercent} />

          <SubHeading>Taxes &amp; carrying costs</SubHeading>
          <FactGrid
            items={[
              { label: 'Annual property tax', value: fmtMoney(val.annual_tax_usd) },
              { label: 'Effective tax rate', value: isNum(val.property_tax_rate_percent) ? fmtPct(val.property_tax_rate_percent, 2) : null },
              { label: 'Assessed value', value: fmtMoney(val.assessed_value_usd) },
              { label: 'HOA fee', value: isNum(val.hoa_fee_usd_per_month) ? `${fmtMoney(val.hoa_fee_usd_per_month)}/mo` : null },
              { label: 'Insurance estimate', value: isNum(val.estimated_insurance_usd_per_year) ? `${fmtMoney(val.estimated_insurance_usd_per_year)}/yr` : null, hint: 'Modeled estimate' },
              { label: 'County', value: identity.county || null },
            ]}
          />

          {(community.community_name || community.neighborhood || community.association_name || community.builder_name) ? (
            <>
              <SubHeading>Community</SubHeading>
              <FactGrid
                items={[
                  { label: 'Community', value: community.community_name },
                  { label: 'Neighborhood', value: community.neighborhood },
                  { label: 'Association', value: community.association_name },
                  { label: 'Builder', value: community.builder_name },
                ].filter((it) => it.value)}
              />
            </>
          ) : null}

          <div className="le-sources">
            {sources.length ? (
              <span className="le-sources-item">Source: {sources.map((s) => <Chip key={s}>{titleCase(s)}</Chip>)}</span>
            ) : null}
            {listingUrl ? (
              <span className="le-sources-item">
                <a href={listingUrl} target="_blank" rel="noopener noreferrer" className="le-link">
                  Open listing record <MdOutlineOpenInNew size={12} />
                </a>
              </span>
            ) : null}
            {data.retrieved_at ? <span className="le-sources-item muted">Retrieved {fmtDate(data.retrieved_at)}</span> : null}
          </div>
        </>
      ) : null}
      {!data && zoning ? <ZoningSection zoning={zoning} status={zoningStatus} /> : null}
      {!data && terrain ? <TerrainSection terrain={terrain} status={terrainStatus} maxSlopePercent={maxSlopePercent} /> : null}
    </SectionCard>
  );
}
